import React from 'react';

import { TableCell, TableRow } from '@material-ui/core';
import { ReactComponent as DeleteIcon } from 'assets/icons/deleteIcon.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { ReactComponent as ResetIcon } from 'assets/pics/resetIcon.svg';
import PropTypes from 'prop-types';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { TooltipIconButton } from 'components/TooltipIconButton/TooltipIconButton';

import {
  BRANCH_TYPE,
  BRANCH_TYPE_TO_VARIABLE_NAME,
  EIV_STATUS,
  EIV_STATUS_ALLOW_EXPERT_EDITING,
  EIV_STATUS_TO_STRING,
  TENANT_ID,
} from 'global/constants';

import {
  areExpertContactsEnabled,
  getExpertStatusEnergy,
  getExpertStatusGas,
} from 'utils/FormUtils';
import {
  canExtendBranch,
  expertHasUnextendedBranch,
  isExpertBranchExtended,
  isRegisteredBranchMain,
} from 'utils/InstallerUtils';
import { getTenantId } from 'utils/tenantUtil';

import { filterExpertForTable } from './utils';

import './ExpertTableRows.sass';

const isMitnetz = getTenantId() === TENANT_ID.MTN;

const getEnergyBirthdate = (row) => {
  if (getTenantId() === TENANT_ID.BAG) {
    return row.birthdate ? row.birthdate : '-';
  }
  return '-';
};
const isExpertDeleted = (row) => {
  const isExpertActiveEnergy = getExpertStatusEnergy(row) !== '-';
  const isExpertActiveGas = getExpertStatusGas(row) !== '-';
  let isDeleted = false;
  if (isExpertActiveEnergy) {
    isDeleted = row.statusEnergy === EIV_STATUS.DELETED;
  }
  if (isExpertActiveGas) {
    isDeleted = row.statusGas === EIV_STATUS.DELETED;
  }
  return isDeleted;
};

const isCellEdited = (oldRow, valuePairs) => {
  if (!oldRow) {
    return true;
  }

  return valuePairs.some(([newValue, oldValue]) => newValue !== oldValue);
};
const getExpertEnergyBranchState = (row, isEnergyBranchExtended) => {
  if (isEnergyBranchExtended) {
    return '(Verlängerung beantragt)';
  }

  return row.statusEnergy !== '-' ? `(${EIV_STATUS_TO_STRING[row.statusEnergy]})` : '-';
};
const getExpertGasBranchState = (row, isGasBranchExtended) => {
  if (isGasBranchExtended) {
    return '(Verlängerung beantragt)';
  }

  return row.statusGas !== '-' ? `(${EIV_STATUS_TO_STRING[row.statusGas]})` : '-';
};

const getRowChangeState = (row, oldRow) => {
  const rowPhoneNumber = row.telefon;
  const rowEnergyStatus = getExpertStatusEnergy(row);
  const oldRowEnergyStatus = getExpertStatusEnergy(oldRow ?? {});
  const rowGasStatus = getExpertStatusGas(row);
  const oldRowGasStatus = getExpertStatusGas(oldRow ?? {});

  const nameValuePairs = [
    [row.completeName, oldRow?.completeName],
    [rowPhoneNumber, oldRow?.telefon],
    [row.netzportalEmail, oldRow?.netzportalEmail],
  ];
  const birthdateValuePairs = [[row.birthdate, oldRow?.birthdate]];
  const energyValuePairs = [
    [rowEnergyStatus, oldRowEnergyStatus],
    [row.expertEnergyValidityDate, oldRow?.expertEnergyValidityDate],
    [row.statusEnergy, oldRow?.statusEnergy],
    [row.isExpertExtendedEnergy, oldRow?.isExpertExtendedEnergy],
  ];
  const gasValuePairs = [
    [rowGasStatus, oldRowGasStatus],
    [row.expertGasValidityDate, oldRow?.expertGasValidityDate],
    [row.statusGas, oldRow?.statusGas],
    [row.isExpertExtendedGas, oldRow?.isExpertExtendedGas],
  ];

  const isNameCellChanged = isCellEdited(oldRow, nameValuePairs);
  const isBirthdateCellChanged = isCellEdited(oldRow, birthdateValuePairs);
  const isEnergyCellChanged = isCellEdited(oldRow, energyValuePairs);
  const isGasCellChanged = isCellEdited(oldRow, gasValuePairs);

  const isEnergyExtensionUpdated = isExpertBranchExtended(row, oldRow, BRANCH_TYPE.ENERGY);
  const isGasExtensionUpdated = isExpertBranchExtended(row, oldRow, BRANCH_TYPE.GAS);

  return {
    rowPhoneNumber,
    rowEnergyStatus,
    rowGasStatus,
    isNameCellChanged,
    isBirthdateCellChanged,
    isEnergyCellChanged,
    isGasCellChanged,
    isEnergyExtensionUpdated,
    isGasExtensionUpdated,
  };
};

const getEditedCellClassName = (edited, deleted) => {
  return edited && !deleted ? 'table-cell-edited' : '';
};

const getTableRowClassName = (row, showMissingWarning, hasUnextendedBranch) => {
  const deleted = isExpertDeleted(row);

  if (deleted) {
    return 'table-row-deleted';
  }

  if (showMissingWarning && hasUnextendedBranch) {
    return 'table-row-warning';
  }

  return '';
};

export const ExpertTableRows = ({
  rows,
  hasEnergy,
  hasGas,
  oldData,
  setSelectedId,
  openExtendPopup,
  openResetExpertBranchPopup,
  openDeletePopup,
  undeleteExpert,
  setOpenEditContactsPopup,
  extendMainExpert,
  installerStatusSummary,
  showMissingWarning,
}) => {
  const extensionButton = (row, branchType, wasBranchExtended) => {
    if (!installerStatusSummary || !row) return null;
    if (wasBranchExtended) {
      return (
        <TooltipIconButton
          className="expert-table__icon-button"
          aria-label="Strom Verlängerung zurücksetzen"
          onClick={() => {
            openResetExpertBranchPopup(row.id, branchType);
          }}>
          <ResetIcon />
        </TooltipIconButton>
      );
    }
    const validityDate = row[`expert${BRANCH_TYPE_TO_VARIABLE_NAME[branchType]}ValidityDate`];
    const canExtend = canExtendBranch(branchType, validityDate);
    if (!canExtend) {
      return null;
    }
    if (
      branchType === BRANCH_TYPE.ENERGY &&
      !EIV_STATUS_ALLOW_EXPERT_EDITING.includes(row.statusEnergy)
    ) {
      return null;
    }
    if (
      branchType === BRANCH_TYPE.GAS &&
      !EIV_STATUS_ALLOW_EXPERT_EDITING.includes(row.statusGas)
    ) {
      return null;
    }
    const isInstallerBranchMain = isRegisteredBranchMain(installerStatusSummary, branchType);

    if (isInstallerBranchMain) {
      return (
        <ActionButton
          title="Verlängern"
          className="align-self-center"
          onClick={() => {
            extendMainExpert(row.id, branchType);
          }}
          inverted
        />
      );
    }
    return (
      <ActionButton
        title="Verlängern"
        className="align-self-center"
        onClick={() => {
          openExtendPopup(row.id, branchType);
        }}
        inverted
      />
    );
  };

  return rows
    .filter((expert) => filterExpertForTable(expert, hasEnergy, hasGas))
    .map((row) => {
      const oldRow = oldData.find((oldRowFilter) => oldRowFilter.id === row.id);
      const deleted = isExpertDeleted(row);
      const {
        rowPhoneNumber,
        rowEnergyStatus,
        rowGasStatus,
        isNameCellChanged,
        isBirthdateCellChanged,
        isEnergyCellChanged,
        isGasCellChanged,
        isEnergyExtensionUpdated,
        isGasExtensionUpdated,
      } = getRowChangeState(row, oldRow);
      const hasUnextendedBranch = expertHasUnextendedBranch(row, oldRow, installerStatusSummary);
      return (
        <TableRow
          key={row.id}
          className={getTableRowClassName(row, showMissingWarning, hasUnextendedBranch)}
          sx={{
            '&:last-child td, &:last-child th': { border: 0 },
          }}>
          <TableCell
            scope="row"
            className={`data expert-table__name-cell ${getEditedCellClassName(
              isNameCellChanged,
              deleted
            )}`}>
            <div className="expert-table__cell-container">
              <div className="expert-table__name-block">
                <span className="full-name">{row.completeName}</span>
                {areExpertContactsEnabled && rowPhoneNumber && (
                  <span className="contact-line">{rowPhoneNumber}</span>
                )}
                {areExpertContactsEnabled && row.netzportalEmail && (
                  <span className="contact-line">{row.netzportalEmail}</span>
                )}
              </div>
              {areExpertContactsEnabled && (
                <TooltipIconButton
                  className="expert-table__icon-button"
                  aria-label="Bearbeiten"
                  onClick={() => {
                    setSelectedId(row.id);
                    setOpenEditContactsPopup(true);
                  }}>
                  <EditIcon />
                </TooltipIconButton>
              )}
            </div>
          </TableCell>
          {getTenantId() === TENANT_ID.BAG ? (
            <TableCell
              align="left"
              className={`data ${getEditedCellClassName(isBirthdateCellChanged, deleted)}`}>
              {getEnergyBirthdate(row)}
            </TableCell>
          ) : null}
          {hasEnergy && (
            <TableCell
              align="left"
              className={`data expert-table__status-cell ${getEditedCellClassName(
                isEnergyCellChanged,
                deleted
              )}`}>
              <div className="expert-table__cell-container">
                <div className="expert-table__status-lines">
                  <span className="status-title">{rowEnergyStatus}</span>
                  <span className="status-date">
                    {rowEnergyStatus !== '-' ? row.expertEnergyValidityDate : '-'}
                  </span>
                  <span className="status-state">
                    {getExpertEnergyBranchState(row, isEnergyExtensionUpdated)}
                  </span>
                </div>
                <div className="expert-table__status-actions">
                  {extensionButton(row, BRANCH_TYPE.ENERGY, isEnergyExtensionUpdated)}
                </div>
              </div>
            </TableCell>
          )}
          {hasGas && (
            <TableCell
              align="left"
              className={`data expert-table__status-cell ${getEditedCellClassName(
                isGasCellChanged,
                deleted
              )}`}>
              <div className="expert-table__cell-container">
                <div className="expert-table__status-lines">
                  <span className="status-title">{rowGasStatus}</span>
                  <span className="status-date">
                    {rowGasStatus !== '-' ? row.expertGasValidityDate : '-'}
                  </span>
                  <span className="status-state">
                    {getExpertGasBranchState(row, isGasExtensionUpdated)}
                  </span>
                </div>
                <div className="expert-table__status-actions">
                  {extensionButton(row, BRANCH_TYPE.GAS, isGasExtensionUpdated)}
                </div>
              </div>
            </TableCell>
          )}
          <TableCell align="center" className="action-buttons">
            <div className="expert-table__button-container">
              {deleted ? (
                <TooltipIconButton
                  className="expert-table__icon-button"
                  aria-label="Fachkraft wiederherstellen"
                  onClick={() => {
                    undeleteExpert(row.id);
                  }}>
                  <ResetIcon />
                </TooltipIconButton>
              ) : (
                <TooltipIconButton
                  className="expert-table__icon-button"
                  aria-label="Fachkraft löschen"
                  onClick={() => {
                    openDeletePopup(row.id);
                  }}>
                  <DeleteIcon />
                </TooltipIconButton>
              )}
            </div>
          </TableCell>
        </TableRow>
      );
    });
};

ExpertTableRows.propTypes = {
  rows: PropTypes.array.isRequired,
  hasEnergy: PropTypes.bool.isRequired,
  hasGas: PropTypes.bool.isRequired,
  oldData: PropTypes.array.isRequired,
  openExtendPopup: PropTypes.func.isRequired,
  openResetExpertBranchPopup: PropTypes.func.isRequired,
  openDeletePopup: PropTypes.func.isRequired,
  undeleteExpert: PropTypes.func.isRequired,
  setOpenEditContactsPopup: PropTypes.func.isRequired,
  extendMainExpert: PropTypes.func.isRequired,
  installerStatusSummary: PropTypes.object.isRequired,
  setSelectedId: PropTypes.func.isRequired,
  showMissingWarning: PropTypes.bool,
};

ExpertTableRows.defaultProps = {
  showMissingWarning: false,
};

ExpertTableRows.displayName = 'ExpertTableRow';
